// src/page/common/ProtectedLayout.js
import React, { createContext, useContext, useMemo, useState, useEffect } from "react";
import { Navigate, Outlet, useLocation, useOutletContext } from "react-router-dom";
import axios from 'axios';
import Menu from "../component/main_menu";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);
export const useAuthContext = () => useOutletContext();

// axios 인터셉터: 모든 요청에 Bearer 토큰 자동 첨부
axios.interceptors.request.use((config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// 401 응답 시 자동 로그아웃 (/api/auth/ 경로는 제외)
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        const url = error.config?.url || '';
        if (error.response && error.response.status === 401 && !url.startsWith('/api/auth/')) {
            localStorage.clear();
            window.location.href = "/";
        }
        return Promise.reject(error);
    }
);

const ProtectedLayout = () => {
    const location = useLocation();
    const [isAuthenticated, setIsAuthenticated] = useState(null); // null = 검증 중
    const [permissions, setPermissions] = useState(null);
    const [features, setFeatures] = useState(null);
    const [loginId, setLoginId] = useState(null);
    const [role, setRole] = useState(null);
    const [loginName, setLoginName] = useState(null);
    const [loginAffiliation, setLoginAffiliation] = useState(null);
    const [loginPosition, setLoginPosition] = useState(null);

    useEffect(() => {
        const validateToken = async () => {
            const token = localStorage.getItem("authToken");
            if (!token) {
                setIsAuthenticated(false);
                return;
            }

            try {
                const response = await axios.get('/api/auth/validate');
                if (response.data.success) {
                    const user = response.data.user;
                    setLoginId(user.id);
                    setRole(user.role || 'staff');
                    setLoginName(user.name);
                    setLoginAffiliation(user.affiliation);
                    setLoginPosition(user.position);
                    setPermissions(user.permissions || null);
                    // 조회 실패 시 features는 null로 남아 IoT 등 환경 한정 메뉴가 숨겨진다.
                    axios.get('/api/config/features')
                        .then((featureResponse) => setFeatures(featureResponse.data))
                        .catch(() => {});
                    setIsAuthenticated(true);
                } else {
                    setIsAuthenticated(false);
                }
            } catch (error) {
                console.error("Token validation failed:", error);
                localStorage.clear();
                setIsAuthenticated(false);
            }
        };
        validateToken();
    }, []);

    const auth = useMemo(
        () => ({
            loginId,
            role,
            loginName,
            loginAffiliation,
            loginPosition,
            permissions,
            features,
            logout: () => {
                const token = localStorage.getItem("authToken");
                if (token) {
                    axios.post('/api/auth/logout').catch(() => {});
                }
                localStorage.clear();
                window.location.href = "/";
            },
        }),
        [loginId, role, loginName, loginAffiliation, loginPosition, permissions, features]
    );

    // 검증 중 → 로딩
    if (isAuthenticated === null) {
        return null;
    }

    // 미인증 → 로그인 페이지
    if (!isAuthenticated) {
        return <Navigate to="/" replace state={{ from: location }} />;
    }

    return (
        <div className="dashboard-container">
            <Menu
                loginId={auth.loginId}
                role={auth.role}
                loginName={auth.loginName}
                loginAffiliation={auth.loginAffiliation}
                loginPosition={auth.loginPosition}
                permissions={permissions}
                features={features}
            />
            <div className="content-container">
                <Outlet key={location.key} context={auth} />
            </div>
        </div>
    );
};

export default ProtectedLayout;
